import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { flushSync } from 'react-dom';
import { Loader, ChevronDown, ChevronRight } from 'lucide-react';

const FLEX_FUEL_TOGGLE_COLLAPSED_CLASS =
  'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700 disabled:hover:border-slate-200 disabled:hover:text-slate-500';
const FLEX_FUEL_TOGGLE_EXPANDED_CLASS =
  'border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:hover:bg-slate-100';
const FLEX_FUEL_TOGGLE_BASE_CLASS =
  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium leading-4';

function buildFlexFuelToggleTitle(isActiveToggle, isGroupBusy, collapsed, label) {
  if (isActiveToggle) return 'Updating chart…';
  if (isGroupBusy) return 'Please wait…';
  if (collapsed) return `Expand ${label} into sub-techs`;
  return `Collapse ${label} to one stack band`;
}

function buildFlexFuelToggleClassName(isGroupBusy, isActiveToggle, collapsed) {
  const busyClass = isGroupBusy && isActiveToggle
    ? 'cursor-wait opacity-80'
    : isGroupBusy
      ? 'cursor-default opacity-55'
      : '';
  const stateClass = collapsed ? FLEX_FUEL_TOGGLE_COLLAPSED_CLASS : FLEX_FUEL_TOGGLE_EXPANDED_CLASS;
  const transitionClass = isGroupBusy ? 'transition-none' : 'transition-colors';
  return `${FLEX_FUEL_TOGGLE_BASE_CLASS} ${transitionClass} ${busyClass} ${stateClass}`.trim();
}

const FlexFuelToggleButton = memo(function FlexFuelToggleButton({
  fuel,
  label,
  collapsed,
  isActiveToggle,
  isGroupBusy,
  onButtonClick,
}) {
  const className = useMemo(
    () => buildFlexFuelToggleClassName(isGroupBusy, isActiveToggle, collapsed),
    [isGroupBusy, isActiveToggle, collapsed]
  );
  const title = useMemo(
    () => buildFlexFuelToggleTitle(isActiveToggle, isGroupBusy, collapsed, label),
    [isActiveToggle, isGroupBusy, collapsed, label]
  );

  return (
    <button
      type="button"
      data-fuel={fuel}
      onClick={onButtonClick}
      disabled={isGroupBusy}
      aria-busy={isActiveToggle || undefined}
      className={className}
      title={title}
      aria-pressed={!collapsed}
    >
      {isActiveToggle ? (
        <Loader className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-500" aria-hidden />
      ) : collapsed ? (
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-500" strokeWidth={2.5} aria-hidden />
      ) : (
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-600" strokeWidth={2.5} aria-hidden />
      )}
      <span>{label}</span>
    </button>
  );
});

/** Coal/Gas/Oil expand — isolated so click feedback paints before heavy chart rebuild. */
const FlexFuelDetailToggleGroup = memo(function FlexFuelDetailToggleGroup({
  flexFuelToggles,
  onToggleFlexFuel,
  flexFuelToggleBusy = false,
  flexFuelToggleActiveFuel = null,
}) {
  const [optimisticActiveFuel, setOptimisticActiveFuel] = useState(null);
  const prevParentBusyRef = useRef(false);
  const optimisticActiveFuelRef = useRef(null);
  const flexFuelToggleBusyRef = useRef(flexFuelToggleBusy);
  const onToggleFlexFuelRef = useRef(onToggleFlexFuel);

  flexFuelToggleBusyRef.current = flexFuelToggleBusy;
  onToggleFlexFuelRef.current = onToggleFlexFuel;
  optimisticActiveFuelRef.current = optimisticActiveFuel;

  useEffect(() => {
    if (flexFuelToggleActiveFuel != null && optimisticActiveFuel != null) {
      setOptimisticActiveFuel(null);
      optimisticActiveFuelRef.current = null;
    }
    if (prevParentBusyRef.current && !flexFuelToggleBusy && flexFuelToggleActiveFuel == null) {
      setOptimisticActiveFuel(null);
      optimisticActiveFuelRef.current = null;
    }
    prevParentBusyRef.current = flexFuelToggleBusy;
  }, [flexFuelToggleBusy, flexFuelToggleActiveFuel, optimisticActiveFuel]);

  const handleButtonClick = useCallback((event) => {
    const fuel = event.currentTarget.dataset.fuel;
    if (!fuel || flexFuelToggleBusyRef.current || optimisticActiveFuelRef.current) return;
    flushSync(() => {
      setOptimisticActiveFuel(fuel);
      optimisticActiveFuelRef.current = fuel;
    });
    requestAnimationFrame(() => {
      const accepted = onToggleFlexFuelRef.current?.(fuel);
      if (accepted === false) {
        setOptimisticActiveFuel(null);
        optimisticActiveFuelRef.current = null;
      }
    });
  }, []);

  const displayedActiveFuel = useMemo(
    () => flexFuelToggleActiveFuel ?? optimisticActiveFuel,
    [flexFuelToggleActiveFuel, optimisticActiveFuel]
  );
  const isGroupBusy = useMemo(
    () => flexFuelToggleBusy || optimisticActiveFuel != null,
    [flexFuelToggleBusy, optimisticActiveFuel]
  );

  if (!Array.isArray(flexFuelToggles) || flexFuelToggles.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 mb-1.5"
      role="group"
      aria-label="Flex fuel stack detail"
    >
      <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-0.5">
        Flex detail
      </span>
      {flexFuelToggles.map((t) => (
        <FlexFuelToggleButton
          key={t.fuel}
          fuel={t.fuel}
          label={t.label}
          collapsed={t.collapsed}
          isActiveToggle={displayedActiveFuel === t.fuel}
          isGroupBusy={isGroupBusy}
          onButtonClick={handleButtonClick}
        />
      ))}
    </div>
  );
});

export default FlexFuelDetailToggleGroup;
